import * as React from "react"
import { Button as BaseButton } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"
import { XIcon } from "@phosphor-icons/react"

import { cn } from "@/lib/utils"

/* ------------------------------------------------------------------ */
/*  Context — share size/disabled with subcomponents                  */
/* ------------------------------------------------------------------ */

type ChipSize = "xs" | "sm" | "md" | "lg"

interface ChipContextValue {
  size: ChipSize
  disabled: boolean
}

const ChipContext = React.createContext<ChipContextValue>({
  size: "md",
  disabled: false,
})

/* ------------------------------------------------------------------ */
/*  Root                                                              */
/* ------------------------------------------------------------------ */

const chipVariants = cva(
  cn(
    "group/chip inline-flex shrink-0 items-center font-semibold transition-colors select-none",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
    "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50"
  ),
  {
    variants: {
      variant: {
        filled: "",
        outline: "border",
      },
      // Derived from active/hasWarning/hasError (error > warning > active).
      state: {
        default: "",
        active: "",
        warning: "",
        error: "",
      },
      size: {
        xs: "h-6 gap-1 px-2 text-xs [&>svg]:size-3",
        sm: "h-7 gap-1.5 px-3 text-sm [&>svg]:size-3.5",
        md: "h-10 gap-2 px-4 text-base [&>svg]:size-4",
        lg: "h-12 gap-2.5 px-5 text-xl [&>svg]:size-5",
      },
      radius: {
        square: "rounded-lg",
        circle: "rounded-full",
      },
      interactive: {
        true: "cursor-pointer",
        false: "",
      },
    },
    compoundVariants: [
      // -- Filled ---------------------------------------------------------
      {
        variant: "filled",
        state: "default",
        className: "bg-muted text-neutral",
      },
      {
        variant: "filled",
        state: "active",
        className: "bg-primary-pale text-primary",
      },
      {
        variant: "filled",
        state: "warning",
        className: "bg-warning-pale text-warning",
      },
      {
        variant: "filled",
        state: "error",
        className: "bg-error-pale text-destructive",
      },
      // -- Outline --------------------------------------------------------
      {
        variant: "outline",
        state: "default",
        className: "border-border bg-background text-neutral",
      },
      {
        variant: "outline",
        state: "active",
        className: "border-primary bg-primary-pale text-primary",
      },
      {
        variant: "outline",
        state: "warning",
        className: "border-warning bg-warning-pale text-warning",
      },
      {
        variant: "outline",
        state: "error",
        className: "border-destructive bg-error-pale text-destructive",
      },
      // -- Interactive hover (default state only) -------------------------
      {
        interactive: true,
        variant: "filled",
        state: "default",
        className: "hover:bg-border",
      },
      {
        interactive: true,
        variant: "outline",
        state: "default",
        className: "hover:bg-muted",
      },
    ],
    defaultVariants: {
      variant: "filled",
      state: "default",
      size: "md",
      radius: "square",
      interactive: false,
    },
  }
)

export type ChipVariantProps = VariantProps<typeof chipVariants>

export interface ChipProps
  extends
    Omit<React.ComponentProps<"span">, "onClick">,
    Pick<ChipVariantProps, "variant" | "size" | "radius"> {
  /** Whether the chip is disabled. */
  disabled?: boolean
  /** Active/selected state (filter chips). */
  active?: boolean
  /** Render the chip in an error state. Takes precedence over `hasWarning` and `active`. */
  hasError?: boolean
  /** Render the chip in a warning state. Takes precedence over `active`. */
  hasWarning?: boolean
  /** Show the trailing remove (X) button. */
  showRemove?: boolean
  /** Accessible label for the remove button. Defaults to "Remove". */
  removeLabel?: string
  /** Handler fired when the remove button is activated. */
  onRemove?: React.MouseEventHandler<HTMLButtonElement>
  /** Render the chip as an interactive button. Automatically true when `onClick` is set. */
  asButton?: boolean
  /** Click handler — when set, the chip becomes interactive. */
  onClick?: React.MouseEventHandler<HTMLElement>
}

function resolveState({
  hasError,
  hasWarning,
  active,
}: Pick<ChipProps, "hasError" | "hasWarning" | "active">): NonNullable<
  ChipVariantProps["state"]
> {
  if (hasError) return "error"
  if (hasWarning) return "warning"
  if (active) return "active"
  return "default"
}

function Chip({
  className,
  variant = "filled",
  size = "md",
  radius = "square",
  disabled = false,
  active = false,
  hasError = false,
  hasWarning = false,
  showRemove = false,
  removeLabel = "Remove",
  onRemove,
  asButton,
  onClick,
  children,
  ...props
}: ChipProps) {
  const interactive = Boolean(asButton ?? onClick)
  const state = resolveState({ hasError, hasWarning, active })

  const ctx = React.useMemo<ChipContextValue>(
    () => ({ size: size ?? "md", disabled }),
    [size, disabled]
  )

  const sharedProps = {
    "data-slot": "chip",
    "data-variant": variant,
    "data-state": state,
    "data-size": size,
    "data-radius": radius,
    "data-disabled": disabled,
    "data-active": active,
    className: cn(
      chipVariants({ variant, state, size, radius, interactive }),
      className
    ),
  }

  const remove = showRemove ? (
    <ChipRemove label={removeLabel} onClick={onRemove} />
  ) : null

  return (
    <ChipContext.Provider value={ctx}>
      {interactive ? (
        <BaseButton
          {...(sharedProps as React.ComponentProps<typeof BaseButton>)}
          disabled={disabled}
          aria-pressed={active}
          onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
          {...(props as React.ComponentProps<typeof BaseButton>)}
        >
          {children}
          {remove}
        </BaseButton>
      ) : (
        <span {...sharedProps} {...props}>
          {children}
          {remove}
        </span>
      )}
    </ChipContext.Provider>
  )
}

/* ------------------------------------------------------------------ */
/*  ChipAvatar — wraps an Avatar so it fits the chip height            */
/* ------------------------------------------------------------------ */

function ChipAvatar({
  className,
  children,
  ...props
}: React.ComponentProps<"span">) {
  const { size } = React.useContext(ChipContext)
  return (
    <span
      data-slot="chip-avatar"
      className={cn(
        "-ml-1 inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full",
        size === "xs" && "mr-0.5 size-3.5",
        size === "sm" && "mr-0.5 size-4",
        size === "md" && "mr-1 size-6",
        size === "lg" && "mr-1.5 size-8",
        "**:data-[slot=avatar]:size-full **:data-[slot=avatar]:after:hidden",
        className
      )}
      {...props}
    >
      {children}
    </span>
  )
}

/* ------------------------------------------------------------------ */
/*  ChipIcon — leading or trailing icon slot                          */
/* ------------------------------------------------------------------ */

function ChipIcon({
  className,
  children,
  ...props
}: React.ComponentProps<"span">) {
  const { size } = React.useContext(ChipContext)
  return (
    <span
      data-slot="chip-icon"
      className={cn(
        "inline-flex shrink-0 items-center justify-center [&>svg]:size-full",
        size === "xs" && "mr-0.5",
        size === "sm" && "mr-0.5",
        size === "md" && "mr-1",
        size === "lg" && "mr-1.5",
        className
      )}
      {...props}
    >
      {children}
    </span>
  )
}

/* ------------------------------------------------------------------ */
/*  ChipRemove — dismiss button (X), rendered via `showRemove`        */
/* ------------------------------------------------------------------ */

interface ChipRemoveProps extends Omit<
  React.ComponentProps<typeof BaseButton>,
  "children"
> {
  label?: string
}

function ChipRemove({
  className,
  label = "Remove",
  onClick,
  ...props
}: ChipRemoveProps) {
  const { size, disabled } = React.useContext(ChipContext)
  return (
    <BaseButton
      data-slot="chip-remove"
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation()
        onClick?.(event)
      }}
      className={cn(
        "-mr-1 inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full opacity-70 transition-opacity",
        "hover:bg-foreground/10 hover:opacity-100",
        "focus-visible:outline-2 focus-visible:outline-current",
        "disabled:pointer-events-none",
        size === "xs" && "size-4 [&>svg]:size-3",
        size === "sm" && "size-5 [&>svg]:size-3.5",
        size === "md" && "size-6 [&>svg]:size-4",
        size === "lg" && "size-7 [&>svg]:size-4.5",
        className
      )}
      {...props}
    >
      <XIcon weight="bold" />
    </BaseButton>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { Chip, ChipAvatar, ChipIcon, chipVariants }
